// File: src/components/VerifyCode.js
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";


const VerifyCode = () => {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  const codeRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // From /login navigate(..., { state: { contact, method } })
  const contact = location.state?.contact; // email or phone
  const method = location.state?.method;   // "phone" or "email"

  // Guard: redirect to /login if accessed directly (no state)
  useEffect(() => {
    if (!contact || !method) {
      navigate("/login", { replace: true });
      return;
    }
    // For phone, also require a Firebase confirmationResult from the prior step
    if (method === "phone" && !window.confirmationResult) {
      navigate("/login", { replace: true });
    }
  }, [contact, method, navigate]);

  useEffect(() => {
    codeRef.current?.focus();
  }, []);

  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer((s) => s - 1), 1000);
      return () => clearTimeout(t);
    }
    setCanResend(true);
  }, [resendTimer]);

  // ---- Unified verification handler ----
  const handleVerify = async () => {
    setError("");
    const trimmed = code.trim();
    if (trimmed.length !== 6) {
      setError("Please enter a valid 6-digit code.");
      return;
    }

    setSubmitting(true);
    try {
      if (method === "phone") {
        // 1) PHONE: Verify with Firebase (this signs you into Firebase directly)
        const result = await window.confirmationResult.confirm(trimmed);
        const user = result.user;
        console.log("✅ Phone verified user:", user);

        await logLoginAndRoute(user, "phone");
        return;
      }

      // 2) EMAIL CODE: Verify with your server, then sign into Firebase via Custom Token (compat)
      const resp = await fetch("/api/verify-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: contact, code: trimmed }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        throw new Error(data.message || "Verification failed");
      }

      const token = data?.firebase?.customToken;
      if (!token) {
        throw new Error("No Firebase custom token received from server.");
      }

      // Establish Firebase session (COMPAT)
      await window.auth.signInWithCustomToken(token);

      // window.auth.currentUser should now be set
      const fbUser = window.auth.currentUser;
      if (!fbUser) throw new Error("Firebase sign-in did not complete");

      console.log("✅ Email code verified user:", fbUser);
      await logLoginAndRoute(fbUser, "email");
    } catch (err) {
      console.error("❌ Verification error:", err);
      setError(err?.message || "Invalid or expired verification code.");
    } finally {
      setSubmitting(false);
    }
  };

  // Record login method, ensure preferences, route home (or onboarding)
  const logLoginAndRoute = async (user, loginMethod) => {
    try {
      // Best-effort profile/log
      await window.db
        .collection("users")
        .doc(user.uid)
        .set(
          {
            uid: user.uid,
            loginMethod,
            lastLogin: new Date(),
          },
          { merge: true }
        );

      // Preferences check
      const prefRef = window.db.collection("preferences").doc(user.uid);
      const prefDoc = await prefRef.get();

      if (!prefDoc.exists || !prefDoc.data()?.preferencesCompleted) {
        // If you have a dedicated preferences route, send there
        // Otherwise, open your preferences modal later from Home
        navigate("/", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (e) {
      // Never crash the flow if logging fails
      console.warn("Non-fatal post-login flow error:", e?.message || e);
      navigate("/", { replace: true });
    }
  };

  const handleResend = () => {
    // Navigate back to login to re-trigger the send flow; preserve method/contact
    navigate("/login", { state: { prefill: contact, method } });
  };

  return (
    <>
      <style>{`
        .verify-page-wrapper {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          background: linear-gradient(180deg, #f6fbff 0%, #edf6ff 100%);
        }

        .verify-box {
          width: 100%;
          max-width: 420px;
          background: #ffffff;
          border-radius: 20px;
          padding: 28px;
          border: 1px solid rgba(148, 163, 184, 0.18);
          box-shadow: 0 18px 44px rgba(15, 23, 42, 0.10);
          text-align: center;
        }

        .verify-box h2 {
          margin: 0 0 8px;
          font-size: 24px;
          font-weight: 900;
          color: #0f172a;
        }

        .verify-box p {
          margin: 0 0 16px;
          color: #64748b;
          font-size: 14px;
          line-height: 1.5;
          word-break: break-word;
        }

        .verify-box input {
          width: 100%;
          min-height: 50px;
          padding: 12px 16px;
          border: 1px solid #dbe3ee;
          border-radius: 12px;
          font-size: 16px;
          text-align: center;
          letter-spacing: 0.25em;
          margin-bottom: 12px;
          outline: none;
        }

        .verify-box input:focus {
          border-color: rgba(37, 99, 235, 0.42);
          box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.10);
        }

        .verify-box > button {
          width: 100%;
          min-height: 48px;
          padding: 12px;
          border: none;
          border-radius: 12px;
          background: linear-gradient(135deg, #1d4ed8, #1554e8);
          color: #fff;
          font-weight: 900;
          font-size: 16px;
          cursor: pointer;
        }

        .verify-box > button:disabled {
          background: #cbd5e1;
          cursor: not-allowed;
        }

        .resend-info {
          margin-top: 16px;
          font-size: 14px;
          color: #64748b;
        }

        .resend-btn {
          background: transparent;
          border: none;
          color: #2457a6;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          padding: 8px;
          min-height: 36px;
        }

        .verify-box .error-message {
          margin-bottom: 12px;
        }

        @media (max-width: 480px) {
          .verify-box {
            padding: 22px 18px;
            border-radius: 16px;
          }

          .verify-box h2 {
            font-size: 22px;
          }

          .verify-box input {
            min-height: 54px;
          }
        }
      `}</style>
      <div className="verify-page-wrapper">
        <div className="verify-box">
        <h2>We sent you a code</h2>
        <p>
          Enter it below to verify <strong>{contact}</strong>.
        </p>

        {error && <div className="error-message">{error}</div>}

        {/* Show a 6-digit code input for BOTH phone and email code flows */}
        <input
          ref={codeRef}
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="6-digit code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={submitting}
        />
        <button onClick={handleVerify} disabled={submitting}>
          {submitting ? "Verifying…" : "Next"}
        </button>

        <p className="resend-info" style={{ marginTop: 12 }}>
          {!canResend ? (
            <>Resend available in {resendTimer}s</>
          ) : (
            <button onClick={handleResend} className="resend-btn">
              Resend Code
            </button>
          )}
        </p>

        {/* 
        {method !== "phone" && (
          <>
            <p style={{ fontStyle: "italic", color: "#555" }}>
              If you used an email verification link earlier, click it in your inbox,
              then return and press the button below.
            </p>
            <button onClick={handleEmailVerify}>I have verified</button>
            <button onClick={handleResendEmailVerification}>Resend Email</button>
          </>
        )}
        */}
      </div>
    </div>
  </>
  );
};

export default VerifyCode;
